<script setup lang="ts">
import type { User } from "firebase/auth";

const { $firebaseAuth } = useNuxtApp();
const { t, locale: currentLocale } = useI18n();
const localePath = useLocalePath();

const user = useUser();
const loginAsUser = useLoginAsUser();
const colorMode = useColorMode();
const theme = useTheming();
const isFeedbackDrawerOpened = ref(false);
const activeSection = ref<"language" | "appearance" | "">("");

async function signOut() {
  await loginAsUser.clear();
  await logoutUser();
  navigateTo("/auth/login/");
}

function openMenuSection(section?: "language" | "appearance") {
  activeSection.value = section || "";
}

async function switchBack() {
  const lastLocation = loginAsUser.state?.lastLocation || "/";

  await loginAsUser.clear();
  await clearMemoizedCaches();
  await updateUser($firebaseAuth.currentUser as User);
  await preloadUserData();

  reloadNuxtApp({ path: lastLocation });
}
</script>

<template>
  <UserMenuDrawerFeedback v-if="isFeedbackDrawerOpened" @close="isFeedbackDrawerOpened = false" />

  <UMenu>
    <template v-if="user" #trigger="{ toggle }">
      <UButton
        variant="link-secondary"
        :aria-label="t('labels.account_settings')"
        @click.stop.prevent="toggle"
      >
        <UAvatar
          :alt="user.fullName || 'Anonymous'"
          :size="40"
          class="u-user-avatar"
          :marker="loginAsUser.state ? 'switch-back' : undefined"
        />
      </UButton>
    </template>
    <template #default="{ hide }">
      <div v-auto-animate class="overflow-hidden bg-white dark:bg-neutral-dark-700 md:w-80">
        <template v-if="activeSection">
          <UserMenuSelectionTheme v-if="activeSection === 'appearance'" @back="openMenuSection()" />
          <UserMenuSelectionLanguage v-else-if="activeSection === 'language'" @back="openMenuSection()" />
          <UListItem
            v-else
            size="lg"
            :text="t('labels.back')"
            aria-label="Back"
            :icon="{ name: 'arrow-right', class: 'transform rotate-180 text-black dark:text-white' }"
            @click="openMenuSection()"
          />
        </template>
        <UList v-else>
          <UserMenuUserRow />
          <UListItem
            v-if="user && loginAsUser.state"
            size="md"
            icon="switch-to"
            :text="t('labels.switch_back')"
            aria-label="Switch back"
            @click="switchBack()"
          />
          <UListItem
            v-if="user && !loginAsUser.state"
            size="md"
            icon="profile"
            :text="t('labels.account_settings')"
            :to="localePath('/account/general/')"
            aria-label="Account settings"
          />
          <UListItem
            :on-click-close-menu="false"
            size="md"
            icon="globe"
            :text="t('labels.selected_language', { value: t(`locales.${currentLocale}`) })"
            aria-label="Change language"
            trailing-icon="chevron-right-small"
            @click="openMenuSection('language')"
          />
          <UListItem
            :on-click-close-menu="false"
            size="md"
            icon="mode"
            :text="t('labels.selected_appearance', { value: t(`labels.theme_${colorMode.preference}`) })"
            aria-label="Change appearance"
            trailing-icon="chevron-right-small"
            @click="openMenuSection('appearance')"
          />
          <UListItem
            size="md"
            icon="info"
            :text="t('labels.help_and_support')"
            :href="theme.helpAndSupportURL"
            target="_blank"
            aria-label="Help and support"
          />
          <UListItem
            v-if="user"
            size="md"
            icon="feedback"
            :text="t('labels.send_feedback')"
            aria-label="Send feedback"
            @click="hide(); isFeedbackDrawerOpened = true;"
          />
          <UListItem
            v-if="user"
            size="md"
            :icon="{ name: 'logout', class: 'text-red-500' }"
            :text="t('labels.logout')"
            aria-label="Logout"
            @click="signOut()"
          >
            <span class="text-red-500">Logout</span>
          </UListItem>
        </UList>
      </div>
    </template>
  </UMenu>
</template>
